import dayjs from 'dayjs';

export const taxQuarterOptions = [
    { value: 1, label: 'Q1' },
    { value: 2, label: 'Q2' },
    { value: 3, label: 'Q3' },
    { value: 4, label: 'Q4' },
];

/**
 * The income period each estimated-tax quarter covers. These are the IRS estimated-tax periods,
 * which are deliberately uneven (Q2 is two months, Q4 is four) and are NOT calendar quarters — so
 * they can't be derived from the quarter number arithmetically.
 */
const quarterIncomePeriod: Record<number, { start: string; end: string }> = {
    1: { start: 'January 1', end: 'March 31' },
    2: { start: 'April 1', end: 'May 31' },
    3: { start: 'June 1', end: 'August 31' },
    4: { start: 'September 1', end: 'December 31' },
};

/**
 * Human label for the income period a quarter's estimate covers, e.g. "April 1 - May 31, 2026".
 * Returns an empty string for an out-of-range quarter so callers render nothing rather than
 * "undefined - undefined".
 */
export const getQuarterIncomePeriod = (year: number, quarter: number): string => {
    const period = quarterIncomePeriod[quarter];
    if (!period) {
        return '';
    }
    return `${period.start} - ${period.end}, ${year}`;
};

/**
 * Converts any date format to YYYY-MM-DD for backend API
 * Handles MM/DD/YYYY format (user input) and YYYY-MM-DD format (existing data)
 */
export const convertDateToBackendFormat = (dateString: string | undefined): string | undefined => {
    if (!dateString) {
        return dateString;
    }

    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
    }

    // If in MM/DD/YYYY format, convert to YYYY-MM-DD
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
        return dayjs(dateString, 'MM/DD/YYYY').format('YYYY-MM-DD');
    }

    // For any other format, try generic parsing
    return dayjs(dateString).format('YYYY-MM-DD');
};

/**
 * Converts backend date format (YYYY-MM-DD) to frontend format (MM/DD/YYYY)
 * If date is already in MM/DD/YYYY format or empty, returns as is
 * This is the inverse of convertDateToBackendFormat
 */
export const convertDateToFrontendFormat = (dateString: string | undefined): string => {
    if (!dateString) {
        return '';
    }

    // If already in MM/DD/YYYY format, return as is
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
        return dateString;
    }

    // If in YYYY-MM-DD format (backend format), convert to MM/DD/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dayjs(dateString).format('MM/DD/YYYY');
    }

    // Try to parse any other format and convert to MM/DD/YYYY
    const parsed = dayjs(dateString);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY') : dateString;
};

export const getCurrentQuarter = () => {
    const currentMonth = dayjs().month() + 1;

    if (currentMonth <= 3) {
        return 1;
    }

    if (currentMonth <= 6) {
        return 2;
    }

    if (currentMonth <= 9) {
        return 3;
    }

    return 4;
};

export const getCurrentYear = () => {
    return dayjs().year();
};

// Q1 (Jan–Mar): due Apr 15 → payments made Feb-Apr apply to Q1
// Q2 (Apr–May): due Jun 15 → payments made May–Jun apply to Q2
// Q3 (Jun–Aug): due Sep 15 → payments made Jul–Sep apply to Q3
// Q4 (Sep–Dec): due Jan 15 (next year) → payments made Oct-Jan apply to Q4
// For users to record payments for previous quarters in the current year
// In Q4, they could only record payments for Q1-Q3
export const getPaymentQuarterDueDate = (year: number, quarter: number): dayjs.Dayjs => {
    const quarterCalcPeriodEndMap: Record<number, dayjs.Dayjs> = {
        1: dayjs().year(year).month(3).date(15),
        2: dayjs().year(year).month(5).date(15),
        3: dayjs().year(year).month(8).date(15),
    };

    return quarterCalcPeriodEndMap[quarter];
};

const US_HOLIDAYS_2025 = new Set([
    '2025-01-01', // New Year's Day
    '2025-01-20', // MLK Day
    '2025-02-17', // Presidents Day
    '2025-05-26', // Memorial Day
    '2025-07-04', // Independence Day
    '2025-09-01', // Labor Day
    '2025-10-13', // Columbus Day
    '2025-11-11', // Veterans Day
    '2025-11-27', // Thanksgiving
    '2025-12-25', // Christmas
]);

const isWeekend = (date: dayjs.Dayjs): boolean => [0, 6].includes(date.day());

const isHoliday = (date: dayjs.Dayjs): boolean => US_HOLIDAYS_2025.has(date.format('YYYY-MM-DD'));

const getNextBusinessDay = (date: dayjs.Dayjs): dayjs.Dayjs => {
    let next = date;
    while (isWeekend(next) || isHoliday(next)) {
        next = next.add(1, 'day');
    }
    return next;
};

const formatDate = (date: dayjs.Dayjs): string => date.format('MMM D');

/**
 * Abbreviated, business-day-adjusted due label ("Jun 15"). Module-private: the wizard was its only
 * outside caller, and `getFullMonthQuarterDueDate` below is what the dashboard's DUE pills use.
 */
const getQuarterDueDate = (
    year: number,
    quarter: number,
    paymentSchedule?: { 1: string; 2: string; 3: string; 4: string }
): string => {
    // If a custom payment schedule is provided, use it (expects ISO date format: "YYYY-MM-DD")
    if (paymentSchedule && paymentSchedule[quarter as keyof typeof paymentSchedule]) {
        const customDate = paymentSchedule[quarter as keyof typeof paymentSchedule];
        // The schedule's year is a placeholder (the backend stores 2025): only the month and day are
        // real, so the date is placed in the tax year before rolling past weekends and holidays.
        const baseDate = dayjs(customDate).year(year);
        const dueDate = getNextBusinessDay(baseDate);
        return formatDate(dueDate);
    }

    // Default to standard IRS due dates
    // Q1-Q3 due dates are in the same year, Q4 is due in the next year
    const dueYear = quarter === 4 ? year + 1 : year;

    const dueDatesByQuarter = {
        1: dayjs().year(dueYear).month(3).date(15), // April 15
        2: dayjs().year(dueYear).month(5).date(15), // June 15
        3: dayjs().year(dueYear).month(8).date(15), // September 15
        4: dayjs().year(dueYear).month(0).date(15), // January 15
    };

    const baseDate = dueDatesByQuarter[quarter as keyof typeof dueDatesByQuarter];
    const dueDate = getNextBusinessDay(baseDate);
    return formatDate(dueDate);
};

/**
 * Full-month due label ("June 15") for a quarter, as the QTE dashboard's DUE pills show it.
 * `getQuarterDueDate` returns the abbreviated, business-day-adjusted form ("Jun 15"), so this
 * re-parses its output with the due year rather than duplicating the holiday/weekend logic.
 *
 * Takes the same optional `paymentSchedule` as `getQuarterDueDate`: a jurisdiction's business tax
 * runs on its own schedule, and the fees cards read "Due September 15" like every other DUE pill
 * rather than dropping to the abbreviated form just because a schedule was supplied.
 */
export const getFullMonthQuarterDueDate = (
    year: number,
    quarter: number,
    paymentSchedule?: { 1: string; 2: string; 3: string; 4: string }
): string => {
    const shortLabel = getQuarterDueDate(year, quarter, paymentSchedule);
    /*
     * Re-parsed in the year the short label was computed in: the tax year for a custom schedule (its
     * own year is a placeholder), the next year for the standard Q4 date. Only the month and day are
     * being reformatted, but the year decides whether a Feb 29 survives the round trip.
     */
    const scheduledDate = paymentSchedule?.[quarter as keyof typeof paymentSchedule];
    const standardDueYear = quarter === 4 ? year + 1 : year;
    const dueYear = scheduledDate ? year : standardDueYear;
    const parsed = dayjs(`${shortLabel} ${dueYear}`);
    return parsed.isValid() ? parsed.format('MMMM D') : shortLabel;
};

const getCutoffDate = (year: number, quarter: number): dayjs.Dayjs => {
    // Cutoff dates for when users can continue working on QTE estimates
    const cutoffDatesByQuarter = {
        1: dayjs().year(year).month(4).date(15), // May 15 (continue through 5/15)
        2: dayjs().year(year).month(6).date(31), // July 31 (continue through 7/31)
        3: dayjs().year(year).month(9).date(31), // October 31 (continue through 10/31)
        4: dayjs()
            .year(year + 1)
            .month(1)
            .endOf('month'), // Last day of February (28 or 29)
    };

    return cutoffDatesByQuarter[quarter as keyof typeof cutoffDatesByQuarter];
};

export const getCurrentQTE = (): [year: number, quarter: number] => {
    const currentDate = dayjs();
    const currentYear = currentDate.year();

    // First check if we're in the Q4 cutoff period of the previous year (Jan-Feb of current year)
    const q4CutoffDate = getCutoffDate(currentYear - 1, 4);
    if (currentDate.isBefore(q4CutoffDate) || currentDate.isSame(q4CutoffDate, 'day')) {
        return [currentYear - 1, 4];
    }

    // Check each quarter's cutoff date to determine which QTE is currently active
    for (let quarter = 1; quarter <= 4; quarter++) {
        const cutoffDate = getCutoffDate(currentYear, quarter);
        if (currentDate.isBefore(cutoffDate) || currentDate.isSame(cutoffDate, 'day')) {
            return [currentYear, quarter];
        }
    }

    return [getCurrentYear(), getCurrentQuarter()];
};
